import {
  BottomSheet,
  Input,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics/log";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useScrollToViewOnViewportResize } from "@src/appV2/redesign/utils/useScrollToViewOnViewportResize";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useUpdateWorker } from "@src/appV2/Worker/useUpdateWorker";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";
import { useEffect, useRef } from "react";
import { Controller } from "react-hook-form";

import { BottomSheetHeader } from "../../components/BottomSheetHeader";
import { DialogFooter } from "../../components/DialogFooter";
import { LoadingButton } from "../../components/LoadingButton";
import { TextButton } from "../../components/TextButton";
import { type SsnFormValues, useAddSsn } from "../hooks/useAddSsn";

interface AddSsnBottomSheetProps {
  readonly title: string;
  readonly modalState: UseModalState;
  readonly onSsnAdded: () => void | Promise<void>;
}

const SCROLL_TO_VIEW_DELAY_IN_MS = 50;

export function AddSsnBottomSheet(props: AddSsnBottomSheetProps) {
  const { title, modalState, onSsnAdded } = props;
  const { openArticle } = useZendeskArticle();
  const { whySsnSupportLink } = useSupportLinks();
  const { showErrorToast } = useToast();

  const worker = useDefinedWorker();

  const { mutateAsync: updateWorkerSsn, isLoading: isUpdatingWorkerSsn } = useUpdateWorker();

  const {
    control,
    errors,
    handleChangeSSN,
    handleChangeConfirmSSN,
    handleKeyUpSSN,
    handleKeyUpConfirmSSN,
    handleBlurSSN,
    handleBlurConfirmSSN,
    handleKeyPressSSN,
    handleSaveSSN,
    resetForm,
  } = useAddSsn();

  const ssnFieldRef = useRef<HTMLDivElement>(null);
  const confirmSsnFieldRef = useRef<HTMLDivElement>(null);
  const activeFieldRef = useRef<HTMLElement | undefined>(undefined);

  // when keyboard is shown viewport shrinks, so scroll to the active field
  useScrollToViewOnViewportResize(activeFieldRef, {
    isEnabled: modalState.modalIsOpen,
    delayInMs: SCROLL_TO_VIEW_DELAY_IN_MS,
  });

  useLogEffect(
    APP_V2_USER_EVENTS.ADD_SSN_BOTTOM_SHEET_OPENED,
    { title },
    { enabled: modalState.modalIsOpen }
  );

  useEffect(() => {
    if (modalState.modalIsOpen) {
      resetForm();
    }
  }, [modalState.modalIsOpen, resetForm]);

  async function onSave(ssnNumber: string) {
    try {
      await updateWorkerSsn({
        fullSocialSecurityNumber: ssnNumber,
      });
      modalState.closeModal();
    } catch (error) {
      logError(APP_V2_USER_EVENTS.UPDATE_WORKER_SSN_FAILURE, {
        error,
        metadata: {
          workerId: worker.userId,
        },
      });
      showErrorToast("Unexpected error occurred while saving ssn");
      return;
    }

    logEvent(APP_V2_USER_EVENTS.SSN_UPDATED, { title });
    await onSsnAdded();
  }

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={modalState.closeModal} />}
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            size="large"
            variant="contained"
            isLoading={isUpdatingWorkerSsn}
            onClick={async () => {
              await handleSaveSSN(onSave, (validationErrors) => {
                if (validationErrors.confirmSsn) {
                  logEvent(APP_V2_USER_EVENTS.SSN_MISMATCH_ERROR, { title });
                }

                if (validationErrors.ssn) {
                  logEvent(APP_V2_USER_EVENTS.SSN_VALIDATION_ERROR, { title });
                }

                const errorRef = validationErrors.ssn ? ssnFieldRef : confirmSsnFieldRef;
                setTimeout(() => {
                  errorRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                  });
                }, SCROLL_TO_VIEW_DELAY_IN_MS);
              });
            }}
          >
            Continue
          </LoadingButton>
          <TextButton
            size="large"
            sx={{ marginTop: 1, fontWeight: "bold" }}
            onClick={async () => {
              await openArticle(whySsnSupportLink);
            }}
          >
            Why Clipboard asks for your SSN
          </TextButton>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={7}>
          <Title component="h2" textAlign="center">
            {title}
          </Title>
          <Text>
            We share your Social Security Number only with government and background check agencies
            to ensure compliance with state and federal laws. We securely store an encrypted version
            of your SSN.
          </Text>

          <Stack ref={ssnFieldRef} spacing={3}>
            <Text semibold variant="body2" pl={1}>
              Social Security Number
            </Text>
            <Controller<SsnFormValues, "ssn">
              name="ssn"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  fullWidth
                  size="large"
                  error={Boolean(errors.ssn)}
                  onChange={handleChangeSSN}
                  onKeyUp={handleKeyUpSSN}
                  onKeyPress={handleKeyPressSSN}
                  onBlur={handleBlurSSN}
                  onFocus={(event) => {
                    activeFieldRef.current = event.currentTarget;
                  }}
                />
              )}
            />
            {errors.ssn?.message && (
              <Text variant="caption" color="error" pl={1}>
                {errors.ssn.message}
              </Text>
            )}
          </Stack>

          <Stack ref={confirmSsnFieldRef} spacing={3}>
            <Text semibold variant="body2" pl={1}>
              Confirm Social Security Number
            </Text>
            <Controller<SsnFormValues, "confirmSsn">
              name="confirmSsn"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  fullWidth
                  size="large"
                  error={Boolean(errors.confirmSsn)}
                  onChange={handleChangeConfirmSSN}
                  onKeyUp={handleKeyUpConfirmSSN}
                  onKeyPress={handleKeyPressSSN}
                  onBlur={handleBlurConfirmSSN}
                  onFocus={(event) => {
                    activeFieldRef.current = event.currentTarget;
                  }}
                />
              )}
            />
            {errors.confirmSsn?.message && (
              <Text variant="caption" color="error" pl={1}>
                {errors.confirmSsn.message}
              </Text>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
